import React, { useState, useEffect } from 'react'
import { MultiSelectFilterProps } from './MultiSelectFilter.types'

const useMultiSelectFilterLogic = ({
  options,
  selectedValues,
  onChange,
}: MultiSelectFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState<string[]>(selectedValues || [])
  const [popoverPosition, setPopoverPosition] = useState<{
    anchorOrigin: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' }
    transformOrigin: {
      vertical: 'top' | 'bottom'
      horizontal: 'left' | 'right'
    }
  }>({
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
  })

  // Update local state when prop changes
  useEffect(() => {
    setSelected(selectedValues || [])
  }, [selectedValues])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const buttonRect = event.currentTarget.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const spaceBelow = windowHeight - buttonRect.bottom
    const popoverHeight = 450

    if (spaceBelow < popoverHeight && buttonRect.top > popoverHeight) {
      setPopoverPosition({
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        transformOrigin: { vertical: 'bottom', horizontal: 'right' },
      })
    } else {
      setPopoverPosition({
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
      })
    }

    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleApply = () => {
    onChange(selected.length > 0 ? selected : null)
    handleClose()
  }

  const handleSelectAll = () => {
    setSelected(options)
  }

  const handleSelectNone = () => {
    setSelected([])
  }

  const handleReset = () => {
    setSelected([])
    onChange(null)
    handleClose()
  }

  const handleToggleValue = (value: string) => {
    if (selected.includes(value)) {
      setSelected(selected.filter((v) => v !== value))
    } else {
      setSelected([...selected, value])
    }
  }

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const open = Boolean(anchorEl)
  const id = open ? 'multi-select-filter-popover' : undefined

  return {
    id,
    open,
    anchorEl,
    selected,
    searchTerm,
    filteredOptions,
    popoverPosition,
    handleApply,
    handleClick,
    handleClose,
    handleReset,
    setSearchTerm,
    handleSelectAll,
    handleToggleValue,
    handleSelectNone,
  }
}

export default useMultiSelectFilterLogic
